import accountSecurityRoutes from './account-security';
import addFundsRoutes from './add-funds';
import autoInvestEnableRoutes from './auto-invest-enable';
import { entityTypePath } from '@utils/settings';
import { getItemFromLocalStorage } from '@utils/web-storage';
import { investmentEntity } from '@store/modules/investment-entity';
import { ira } from '@store/modules/ira';
import { orderForm } from '@store/modules/order-form';
import { questionnaire } from '@store/modules/questionnaire';
import riaUpgradeRoutes from './ria-upgrade';
import { RouteConfig } from 'vue-router';
import { tax } from '@store/modules/tax';
import upgradeRoutes from './upgrade';
import { user } from '@store/modules/user';

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const accountRoutes: Array<RouteConfig> = [
	{
		path: `/account`,
		name: 'account-subpage',
		beforeEnter: async (to, from, next) => {
			investmentEntity.storePositions();
			next();
		},
		component: () => import(/* webpackChunkName: "account-subpage" */ '@views/account/account-subpage.vue'),
		redirect: { name: 'account-overview' },
		children: [
			{
				path: 'documents',
				name: 'account-documents',
				beforeEnter: async (to, from, next) => {
					if (investmentEntity.showTaxCenter) {
						tax.storeTaxData();
					}
					next();
				},
				component: () =>
					import(/* webpackChunkName: "account-documents" */ '@views/account/account-documents.vue')
			},
			{
				path: 'portfolio',
				name: 'account-portfolio',
				beforeEnter: async (to, from, next) => {
					investmentEntity.storePositions();
					next();
				},
				component: () =>
					import(/* webpackChunkName: "account-portfolio" */ '@views/account/account-portfolio.vue')
			},
			{
				path: 'portfolio/reit/:reitId',
				name: 'account-portfolio-detail',
				beforeEnter: async (to, from, next) => {
					const isId = /^\d+$/.test(to.params.reitId);
					if (isId) {
						next();
					} else {
						next({ name: 'account-portfolio' });
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "account-portfolio-reit-ipo-detail" */ '@views/account/account-portfolio-reit-ipo-detail.vue'
					)
			},
			{
				path: 'portfolio/note-investment/:noteId',
				name: 'account-portfolio-notes-detail',
				component: () =>
					import(
						/* webpackChunkName: "account-portfolio-note-detail" */ '@views/account/account-portfolio-note-detail.vue'
					)
			},
			{
				path: 'performance',
				beforeEnter: async (to, from, next) => {
					await investmentEntity.storePerformance();
					next();
				},
				name: 'account-performance',
				component: () =>
					import(/* webpackChunkName: "account-performance" */ '@views/account/account-performance.vue')
			},
			{
				path: 'transactions',
				name: 'account-transactions',
				beforeEnter: async (to, from, next) => {
					investmentEntity.storeBankAccounts();
					next();
				},
				component: () =>
					import(/* webpackChunkName: "account-transactions" */ '@views/account/account-transactions.vue')
			}
		]
	},
	{
		path: `/account/overview`,
		name: 'account-overview',
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.hasNeverBeenAnInvestor) {
				next({ name: 'user-overview' });
			} else if (getItemFromLocalStorage('redirectToWelcomeVideo')) {
				next({ name: 'welcome-video' });
			} else {
				investmentEntity.storePositions();
				investmentEntity.storePerformance();
				if (investmentEntity.showTaxCenter) {
					tax.storeTaxData();
				}
				next();
			}
		},
		component: () => import(/* webpackChunkName: "overview" */ '@src/views/account/account-overview.vue')
	},
	{
		path: `/account/user-dashboard/overview`,
		name: 'user-overview',
		beforeEnter: async (to, from, next) => {
			if (!orderForm.dataRetrieved) {
				await orderForm.getUserCheckoutDetailsData();
				const notIraEntity =
					orderForm.signUpCheckoutType === 'IRA' && !investmentEntity.isIraEntity && user.hasIraEntity;
				if (notIraEntity) {
					await orderForm.submitSignupCheckoutType('TAXABLE');
				}
			}
			if (!questionnaire.dataRetrieved && !orderForm.plan) {
				await questionnaire.getFullResponse(true);
			}

			const promises = [investmentEntity.refreshPositionsAndPerformance()];
			if (orderForm.plan) {
				promises.push(investmentEntity.storeReitElementsForPlan(orderForm.plan.id));
			} else if (questionnaire.fullResponse.recommendedRiaPlan) {
				promises.push(
					investmentEntity.storeReitElementsForPlan(questionnaire.fullResponse.recommendedRiaPlan.id)
				);
			}

			if (
				investmentEntity.isIraEntity &&
				investmentEntity.iraEntityMetadata?.isMtcIra &&
				investmentEntity.iraEntityMetadata?.isIraOnboardingFinished
			) {
				promises.push(ira.storeIraFundingType());
			}

			await Promise.all(promises);

			next();
		},
		component: () =>
			import(/* webpackChunkName: "user-overview" */ '@src/views/account/user-overview/user-overview.vue')
	},
	{
		path: '/account/newsfeed',
		name: 'newsfeed',
		beforeEnter: (to, from, next) => {
			if (investmentEntity.hasNeverBeenAnInvestor) {
				next({ name: 'newsfeed-public' });
			} else {
				next();
			}
		},
		component: () => import(/* webpackChunkName: "newsfeed" */ '@views/account/newsfeed.vue')
	},
	{
		path: '/account/user-dashboard/newsfeed',
		name: 'newsfeed-public',
		component: () => import(/* webpackChunkName: "newsfeed-public" */ '@views/account/newsfeed-public.vue')
	},
	{
		path: '/account/performance/account-value-all-time',
		name: 'account-value-all-time',
		beforeEnter: async (to, from, next) => {
			investmentEntity.storePerformance();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "account-performance-all-time" */ '@views/account/account-performance-all-time.vue'
			)
	},
	{
		path: `/account/ineligible-for-drip`,
		name: 'ineligible-for-drip',
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.eligibleForDrip) {
				next({
					name: 'settings-drip-view',
					params: {
						investmentEntityId: investmentEntity.investmentEntityId,
						entityType: entityTypePath(investmentEntity.entityTypeLabel)
					}
				});
			} else {
				next();
			}
		},
		component: () =>
			import(/* webpackChunkName: "ineligible-for-drip" */ '@views/account/drip/ineligible-for-drip.vue')
	},
	{
		path: `/account/investment-accounts/backup-withholding/resolve`,
		name: 'backup-withholding-resolve',
		component: () =>
			import(
				/* webpackChunkName: "backup-withholding-first-notice" */ '@views/backup-withholding/backup-withholding-first-notice.vue'
			)
	},
	{
		path: `/account/investment-accounts/backup-withholding/none-active`,
		name: 'backup-withholding-none-active',
		component: () =>
			import(
				/* webpackChunkName: "backup-withholding-none-active" */ '@views/backup-withholding/backup-withholding-none-active.vue'
			)
	},
	{
		path: `/account/investment-accounts/backup-withholding/in-process`,
		name: 'backup-withholding-in-process',
		component: () =>
			import(
				/* webpackChunkName: "backup-withholding-in-process" */ '@views/backup-withholding/backup-withholding-in-process.vue'
			)
	},
	{
		path: '/account/tax-center',
		name: 'tax-center',
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.showTaxCenter) {
				next({ name: 'account-overview', replace: true });
			} else {
				await tax.storeTaxData();
				next();
			}
		},
		component: () => import(/* webpackChunkName: "account-tax-center" */ '@views/account/account-tax-center.vue')
	},
	{
		path: '/account/update-ssn/form',
		name: 'update-ssn-form',
		beforeEnter: async (to, from, next) => {
			if (user.ssnNeedsUpdating) {
				next();
			} else {
				next({ name: 'update-ssn-save', replace: true });
			}
		},
		component: () => import(/* webpackChunkName: "update-ssn-form" */ '@views/account/update-ssn-form.vue')
	},
	{
		path: '/account/update-ssn/save',
		name: 'update-ssn-save',
		component: () => import(/* webpackChunkName: "update-ssn-save" */ '@views/account/update-ssn-save.vue')
	},
	{
		path: `/account-checklist`,
		name: 'account-checklist',
		component: () => import(/* webpackChunkName: "account-checklist" */ '@views/account/account-checklist.vue')
	},
	...accountSecurityRoutes,
	...addFundsRoutes,
	...autoInvestEnableRoutes,
	...riaUpgradeRoutes,
	...upgradeRoutes
];

export default accountRoutes;
